import * as React from 'react';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import Input from 'components/inputs/input/Input';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import MixedSelectDropdownList from '../list/MixedSelectDropdownList';

import { __t, applyTranslations } from 'utils/language';

import './mixed-select-dropdown-control.scss';

import type { MixedSelectDropdownControlIcons, MixedSelectDropdownItem, TextEntry } from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./mixed-select-dropdown-control.i18n.json'));

type Props = {
  selectedItems: Array<MixedSelectDropdownItem>,
  isMenuOpen: boolean,
  withMulti?: boolean,
  withSearchable?: boolean,
  withRemovable?: boolean,
  withColors?: boolean,
  withIcons?: boolean,
  withTruncate?: boolean,
  withSmallSize?: boolean,
  withTags?: boolean,
  withRandomizedAutocomplete?: boolean,
  hideChevron?: boolean,
  hideSelectedItems?: boolean,
  inputValue: string,
  controlClassName?: string,
  inputClassName?: string,
  placeholder?: string,
  isDisabled?: boolean,
  onInputRefCallback: (a: TextEntry) => void,
  onInputChange: (event: SyntheticEvent<TextEntry>) => void,
  onInputFocus: () => void,
  onInputBlur: () => void,
  onItemsRemove: (a: string) => void,
  onItemsRemoveAll: () => void,
  onMenuToggle: (a: boolean) => void
};

const msgKey = 'mixed_select_dropdown_control';

class MixedSelectDropdownControl extends React.PureComponent<Props> {
  handleInputChange = (event: SyntheticEvent<TextEntry>): void => {
    const { withSearchable, onInputChange } = this.props;

    if (withSearchable) {
      onInputChange(event);
    }
  };

  handleControlClick = (): void => {
    const { isMenuOpen, onInputFocus, onMenuToggle } = this.props;

    onMenuToggle(!isMenuOpen);
    onInputFocus();
  };

  handleIconClick = (event: SyntheticEvent<HTMLElement>): void => {
    const { onItemsRemoveAll } = this.props;
    const target = event.currentTarget;
    const type = target.dataset.type || null;

    if (type === 'clear') {
      onItemsRemoveAll();
      return;
    }

    this.handleControlClick();
  };

  handleItemsRemove = (identifier: string): void => {
    const { onItemsRemove } = this.props;

    onItemsRemove(identifier);
  };

  renderIcon(name: string, type: MixedSelectDropdownControlIcons): React.ReactNode {
    const { isDisabled, withSmallSize } = this.props;
    let size = type === 'toggle' ? 16 : 12;

    if (withSmallSize) {
      size = 8;
    }

    const iconClasses = classnames('mixed-select-dropdown-control__icon', {
      'mixed-select-dropdown-control__icon--is-disabled': isDisabled
    });

    return (
      <div className={iconClasses}>
        <SVGDynamicIcon
          name={name}
          width={size}
          height={size}
          customColor="grey"
          clickable
          data={{ type }}
          onClick={this.handleIconClick}
        />
      </div>
    );
  }

  renderIcons(): React.ReactNode {
    const { selectedItems, withRemovable, hideChevron } = this.props;
    const renderRemoveItemsIcon = !isEmpty(selectedItems) && withRemovable;

    if (!renderRemoveItemsIcon && hideChevron) {
      return null;
    }

    return (
      <div className="mixed-select-dropdown-control__icons">
        {
          !isEmpty(selectedItems) && withRemovable &&
            this.renderIcon('cross-custom-fg', 'clear')
        }
        {
          !hideChevron && this.renderIcon('chevron-down-custom-fg', 'toggle')
        }
      </div>
    );
  }

  renderSelectedItems(): React.ReactNode {
    const {
      selectedItems,
      withMulti,
      withRemovable,
      withColors,
      withIcons,
      withTruncate,
      withSmallSize,
      withTags,
      isDisabled
    } = this.props;

    return (
      <MixedSelectDropdownList
        items={selectedItems}
        withMulti={withMulti}
        withRemovable={withRemovable}
        withColors={withColors}
        withIcons={withIcons}
        withTags={withTags}
        withTruncate={withTruncate}
        withSmallSize={withSmallSize}
        isDisabled={isDisabled}
        onItemsRemove={this.handleItemsRemove}
      />
    );
  }

  renderInput(): React.ReactNode {
    const {
      selectedItems,
      placeholder,
      withSearchable,
      withSmallSize,
      withRandomizedAutocomplete,
      hideChevron,
      inputValue,
      inputClassName,
      isDisabled,
      onInputRefCallback,
      onInputBlur,
      hideSelectedItems
    } = this.props;

    const isSelectedItemsEmpty = isEmpty(selectedItems);

    const fieldClasses = classnames('mixed-select-dropdown-control__field', {
      'mixed-select-dropdown-control__field--is-searchable': withSearchable
    });

    const inputClasses = classnames('mixed-select-dropdown-control__input', {
      'mixed-select-dropdown-control__input--is-searchable': withSearchable,
      'mixed-select-dropdown-control__input--with-small-size': Boolean(withSmallSize),
      [`${String(inputClassName)}`]: Boolean(inputClassName)
    });

    const placeholderClasses = classnames('mixed-select-dropdown-control__placeholder', {
      'mixed-select-dropdown-control__placeholder--with-small-size': Boolean(withSmallSize)
    });

    const placeholderText = (hideChevron)
      ? __t(`${msgKey}.labels.search_only`)
      : __t(`${msgKey}.labels.search_or_select`);

    const autoCompleteSetting = (withRandomizedAutocomplete)
      ? `off-${Math.random()}`
      : 'off';

    return (
      <div
        className={fieldClasses}
        onClick={!isDisabled ? this.handleControlClick : undefined}
      >
        <Input
          // TODO: Change Input.js `ref` implementation to the newer `createRef` style
          // $FlowIgnore - Flow doesn't like that `ref` in Input.js can be `null`
          refCallback={onInputRefCallback}
          type="text"
          name="mixed-select-dropdown-control-input"
          value={inputValue}
          width="responsive"
          placeholder={isSelectedItemsEmpty ? placeholder || placeholderText : ''}
          disabled={isDisabled}
          // Not the coolest way to disable autocomplete, but seems to be the only for the Chrome
          autoComplete={autoCompleteSetting}
          className={inputClasses}
          onChange={this.handleInputChange}
          onBlur={onInputBlur}
        />
        {
          !withSearchable && (isSelectedItemsEmpty || hideSelectedItems) && (
            <div className={placeholderClasses}>
              {placeholder || __t(`${msgKey}.labels.select`)}
            </div>
          )
        }
      </div>
    );
  }

  render(): React.ReactNode {
    const { isDisabled, hideSelectedItems, withSmallSize, controlClassName } = this.props;

    const containerClasses = classnames('mixed-select-dropdown-control', controlClassName, {
      'mixed-select-dropdown-control--is-disabled': isDisabled,
      'mixed-select-dropdown-control--with-small-size': Boolean(withSmallSize)
    });

    const sectionClasses = classnames('mixed-select-dropdown-control__section', {
      'mixed-select-dropdown-control__section--with-small-size': Boolean(withSmallSize)
    });

    return (
      <div className={containerClasses}>
        <div className={sectionClasses}>
          {!hideSelectedItems && this.renderSelectedItems()}
          {this.renderInput()}
        </div>
        {this.renderIcons()}
      </div>
    );
  }
}

export default MixedSelectDropdownControl;
